import { useMemo } from 'react';
import { Select, type SelectProps } from '@mantine/core';
import { useAllLocations } from '../api';
import type { LocationRow } from '../types';

interface AnyLocationSelectProps extends Omit<SelectProps, 'data' | 'value' | 'onChange' | 'searchable'> {
  value: string;
  /** Reports the location's own site id alongside its id/label, since a transfer's
   * from/to SITE is derived from whichever location is picked (see api.ts's
   * `useAllLocations` header comment). */
  onChange: (locationId: string, label: string, siteId: string) => void;
  /** Excludes a location from the option list — used so "to location" can't be the
   * same location already picked as "from location". */
  excludeId?: string;
}

/** Tenant-wide (not site-scoped) location picker for the transfer flow's from/to
 * fields — see `useAllLocations`. */
export function AnyLocationSelect({ value, onChange, excludeId, ...rest }: AnyLocationSelectProps) {
  const { data, isLoading } = useAllLocations();

  const rowsById = useMemo(() => {
    const map = new Map<string, LocationRow>();
    for (const l of data?.items ?? []) map.set(l.id, l);
    return map;
  }, [data]);

  const options = useMemo(
    () =>
      (data?.items ?? [])
        .filter((l) => l.id !== excludeId)
        .map((l) => ({ value: l.id, label: `${l.code} — ${l.name}` })),
    [data, excludeId],
  );

  return (
    <Select
      {...rest}
      searchable
      data={options}
      value={value || null}
      description={rest.description ?? 'Picking a location also sets its site — no separate site field needed'}
      onChange={(newValue) => {
        const row = newValue ? rowsById.get(newValue) : undefined;
        onChange(newValue ?? '', row ? `${row.code} — ${row.name}` : '', row?.siteId ?? '');
      }}
      nothingFoundMessage={isLoading ? 'Loading…' : 'No locations found'}
      comboboxProps={{ withinPortal: true }}
    />
  );
}
